'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Monitor, Users, Loader2, Layers, CircleQuestionMark, MonitorCog } from "lucide-react";
import { useRouter } from 'next/navigation';
import { useTheme } from "next-themes";
import { createPortal } from 'react-dom';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { api } from "@/trpc/react";
import { type Session } from "@/types/auth";
import { useAtom } from 'jotai';
import { selectedCustomerAtom } from './CustomerSelector';
import { userIsUniversal, userIsUniversalAdmin } from '@/utils/accessControl';

// Search button that can be placed in the navbar
export function SearchButton({ onClick, session }: { onClick: () => void, session: Session | null }) {
    if (!session) return null;
    return (
        <Button
            variant="ghost"
            className="justify-start px-2 py-2 h-12 transition-colors"
            onClick={onClick}
        >
            <Search className="h-4 w-4" aria-hidden="true" />
            <kbd className="pointer-events-none ml-1.5 hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
                /
            </kbd>
        </Button>
    );
}

// Utility function to highlight matching text
const HighlightMatch = ({ text, query }: { text: string; query: string }) => {
    if (!text || !query.trim()) return <>{text}</>;

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    const parts = text.split(regex);

    return (
        <>
            {parts.map((part, i) =>
                regex.test(part) ? (
                    <span key={i} className="bg-primary text-primary-foreground font-medium px-0.5">
                        {part}
                    </span>
                ) : part
            )}
        </>
    );
};

export function UnifiedSearch({
    session,
    onSearchActiveChange,
    open: externalOpen,
    onOpenChange
}: {
    session: Session | null;
    onSearchActiveChange?: (active: boolean) => void;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}) {
    const [internalOpen, setInternalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [, setSelectedCustomer] = useAtom(selectedCustomerAtom);
    const router = useRouter();
    const commandRef = useRef<HTMLDivElement>(null);
    const { resolvedTheme } = useTheme();

    // Determine if controlled externally or internally
    const isControlled = externalOpen !== undefined && onOpenChange !== undefined;
    const open = isControlled ? externalOpen : internalOpen;
    const setOpen = useCallback((value: boolean) => {
        if (isControlled) {
            onOpenChange?.(value);
        } else {
            setInternalOpen(value);
        }
    }, [isControlled, onOpenChange]);

    // Add debounce for search query
    const [debouncedQuery, setDebouncedQuery] = useState('');

    // Notify parent component when search is active
    useEffect(() => {
        onSearchActiveChange?.(open);
    }, [open, onSearchActiveChange]);

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(searchQuery.slice(0, 50));
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Search devices
    const { data: deviceData, isError: deviceError, isLoading: deviceLoading } = api.device.search.useQuery(
        { query: debouncedQuery },
        {
            enabled: open && debouncedQuery.length > 2,
            staleTime: 1000 * 60,
            refetchOnWindowFocus: false,
        }
    );

    // Search customers
    const { data: customerData, isError: customerError, isLoading: customerLoading } = api.customer.search.useQuery(
        { query: debouncedQuery },
        {
            enabled: open && debouncedQuery.length > 2,
            staleTime: 1000 * 60,
            refetchOnWindowFocus: false,
        }
    );

    // Search groups
    const { data: groupData, isError: groupError, isLoading: groupLoading } = api.group.search.useQuery(
        { query: debouncedQuery },
        {
            enabled: open && debouncedQuery.length > 2,
            staleTime: 1000 * 60,
            refetchOnWindowFocus: false,
        }
    );

    // Search device configs
    const { data: configData, isError: configError, isLoading: configLoading } = api.deviceConfig.search.useQuery(
        { query: debouncedQuery },
        {
            enabled: open && userIsUniversalAdmin(session) && debouncedQuery.length > 2,
            staleTime: 1000 * 60,
            refetchOnWindowFocus: false,
        }
    );

    // Search unknown devices
    const { data: unknownDeviceData, isError: unknownDeviceError, isLoading: unknownDeviceLoading } = api.deviceConfig.searchUnknown.useQuery(
        { query: debouncedQuery },
        {
            enabled: open && userIsUniversalAdmin(session) && debouncedQuery.length > 2,
            staleTime: 1000 * 60,
            refetchOnWindowFocus: false,
        }
    );

    // Navigate to application page
    const navigateTo = useCallback((path: string) => {
        setOpen(false);
        router.push(path);
    }, [router, setOpen]);

    // Handle keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const isInputField = document.activeElement instanceof HTMLInputElement ||
                document.activeElement instanceof HTMLTextAreaElement;

            // Slash key to open search
            if (e.key === '/' && !isInputField) {
                e.preventDefault();
                setOpen(true);
            }

            // Close search on Escape key
            if (e.key === 'Escape' && open) {
                e.preventDefault();
                setOpen(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [open, navigateTo, session, setOpen]);

    // Handle clicks outside the command dialog
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (open && commandRef.current && !commandRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open, setOpen]);

    // Clear search query when dialog closes
    useEffect(() => {
        if (!open) {
            setSearchQuery('');
        }
    }, [open]);

    // Handle selection of search results
    const handleSelect = useCallback((type: 'device' | 'customer' | 'group' | 'device_config', id: string | undefined) => {
        setOpen(false);
        if (!id) return;

        if (type === 'device') {
            router.push(`/device/${id}`);
        } else if (type === 'customer') {
            setSelectedCustomer(id);
        } else if (type === 'group') {
            router.push(`/group/${id}`);
        } else if (type === 'device_config') {
            setSelectedCustomer(id);
            router.push(`/deviceconfigurations`);
        }
    }, [router, setSelectedCustomer, setOpen]);

    if (!session) return null;

    return (
        <>
            {open && createPortal(
                <div className={`fixed left-0 right-0 top-16 mx-auto w-full max-w-4xl px-4 z-50 ${resolvedTheme === 'dark' ? 'dark' : ''}`}>
                    <Command
                        ref={commandRef}
                        className="rounded-lg border shadow-md bg-popover border-border text-popover-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 w-full"
                        loop={true}
                    >
                        <CommandInput
                            className="focus:ring-0 focus:ring-offset-0 focus:outline-none border-none text-popover-foreground placeholder:text-muted-foreground"
                            value={searchQuery}
                            onValueChange={(value) => setSearchQuery(value.slice(0, 50))}
                            placeholder={(userIsUniversal(session) || (session?.customers && session.customers.length > 1))
                                ? "Search devices, groups, and customers..."
                                : "Search devices and groups..."}
                            autoFocus
                        />
                        <CommandList className="overflow-y-auto max-h-72 focus:outline-none bg-popover">
                            {searchQuery.length <= 2 && (
                                <CommandEmpty className="p-6 text-sm text-muted-foreground">Enter at least 3 characters to search</CommandEmpty>
                            )}

                            {searchQuery.length > 2 && debouncedQuery.length > 2 && (deviceLoading || customerLoading || groupLoading || configLoading || unknownDeviceLoading) && (
                                <div className="flex items-center justify-center p-6 text-sm text-muted-foreground">
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    <span>Searching...</span>
                                </div>
                            )}

                            {debouncedQuery.length > 2 && (deviceError || customerError || groupError || configError || unknownDeviceError) && (
                                <div className="flex items-center justify-center p-6 text-sm text-destructive">
                                    <span>Error loading results. Please try again.</span>
                                </div>
                            )}

                            {debouncedQuery.length > 2 && !deviceError && !customerError && !groupError && !configError && !unknownDeviceError &&
                                ((!deviceData || !Array.isArray(deviceData) || deviceData.length === 0) &&
                                    (!customerData || !Array.isArray(customerData) || customerData.length === 0) &&
                                    (!groupData || !Array.isArray(groupData) || groupData.length === 0) &&
                                    (!configData || !Array.isArray(configData) || configData.length === 0) &&
                                    (!unknownDeviceData || !Array.isArray(unknownDeviceData) || unknownDeviceData.length === 0)) && (
                                    <CommandEmpty className="p-6 text-sm text-muted-foreground">No results found</CommandEmpty>
                                )}

                            {deviceData && deviceData.length > 0 && (
                                <CommandGroup heading={`Devices (${deviceData.length})`} className="py-1 px-1 text-muted-foreground" forceMount>
                                    {deviceData.map((device) => (
                                        <CommandItem
                                            key={device.id ?? device.serial_number ?? device.kuori_serial ?? 'unknown'}
                                            onSelect={() => handleSelect('device', device.id ?? '')}
                                        >
                                            <div className="flex items-center cursor-pointer">
                                                <Monitor className="mr-2 h-4 w-4 shrink-0" />
                                                <div className="flex flex-col overflow-hidden">
                                                    <span className="truncate">
                                                        <HighlightMatch text={device.name ?? 'Unnamed Device'} query={debouncedQuery} />
                                                    </span>
                                                    <div className="flex flex-col text-xs text-muted-foreground">
                                                        <span className="truncate">
                                                            <HighlightMatch text={device.serial_number ?? device.kuori_serial ?? device.id ?? 'No ID'} query={debouncedQuery} />
                                                        </span>
                                                        {(userIsUniversal(session) || (session?.customers && session.customers.length > 1)) && device.customers && (
                                                            <span className="truncate">
                                                                {device.customers.name}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            )}
                            {groupData && groupData.length > 0 && (
                                <>
                                    {deviceData && deviceData.length > 0 && <CommandSeparator />}
                                    <CommandGroup heading={`Groups (${groupData.length})`} className="py-1 px-1 text-muted-foreground" forceMount>
                                        {groupData.map((group) => (
                                            <CommandItem
                                                key={group.id ?? 'unknown-group'}
                                                onSelect={() => handleSelect('group', group.id ?? '')}
                                            >
                                                <div className="flex items-center cursor-pointer">
                                                    <Layers className="mr-2 h-4 w-4 shrink-0" />
                                                    <div className="flex flex-col overflow-hidden">
                                                        <span className="truncate">
                                                            <HighlightMatch text={group.name} query={debouncedQuery} />
                                                        </span>
                                                        {(userIsUniversal(session) || (session?.customers && session.customers.length > 1)) && group.customers && (
                                                            <span className="text-xs text-muted-foreground truncate">
                                                                {group.customers.name}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </>
                            )}
                            {customerData && customerData.length > 0 && (
                                <>
                                    {deviceData && deviceData.length > 0 && <CommandSeparator />}
                                    <CommandGroup heading={`Customers (${customerData.length})`} className="py-1 px-1 text-muted-foreground" forceMount>
                                        {customerData.map((customer) => (
                                            <CommandItem
                                                key={customer.id ?? 'unknown-customer'}
                                                onSelect={() => handleSelect('customer', customer.id ?? '')}
                                            >
                                                <div className="flex items-center cursor-pointer">
                                                    <Users className="mr-2 h-4 w-4 shrink-0" />
                                                    <span className="truncate">
                                                        <HighlightMatch text={customer.name} query={debouncedQuery} />
                                                    </span>
                                                </div>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </>
                            )}
                            {userIsUniversalAdmin(session) && configData && configData.length > 0 && (
                                <>
                                    {(deviceData && deviceData.length > 0) || (groupData && groupData.length > 0) || (customerData && customerData.length > 0) ? <CommandSeparator /> : null}
                                    <CommandGroup heading={`Device Configurations (${configData.length})`} className="py-1 px-1 text-muted-foreground" forceMount>
                                        {configData.map((config) => (
                                            <CommandItem
                                                key={config.id ?? 'unknown-config'}
                                                onSelect={() => handleSelect('device_config', config.customers.id ?? '')}
                                            >
                                                <div className="flex items-center cursor-pointer">
                                                    <MonitorCog className="mr-2 h-4 w-4 shrink-0" />
                                                    <div className="flex flex-col overflow-hidden">
                                                        <span className="truncate">
                                                            <HighlightMatch text={config.serial_number ?? 'Unnamed Config'} query={debouncedQuery} />
                                                        </span>
                                                        {config.customers && (
                                                            <span className="text-xs text-muted-foreground truncate">
                                                                {config.customers.name}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </>
                            )}
                            {userIsUniversalAdmin(session) && unknownDeviceData && unknownDeviceData.length > 0 && (
                                <>
                                    {(deviceData && deviceData.length > 0) || (groupData && groupData.length > 0) || (customerData && customerData.length > 0) || (configData && configData.length > 0) ? <CommandSeparator /> : null}
                                    <CommandGroup heading={`Unknown Devices (${unknownDeviceData.length})`} className="py-1 px-1 text-muted-foreground" forceMount>
                                        {unknownDeviceData.map((device) => (
                                            <CommandItem
                                                key={device.id ?? device.serial_number ?? 'unknown-device'}
                                                onSelect={() => navigateTo(`/unknowndevices`)}
                                            >
                                                <div className="flex items-center cursor-pointer">
                                                    <CircleQuestionMark className="mr-2 h-4 w-4 shrink-0" />
                                                    <div className="flex flex-col overflow-hidden">
                                                        <span className="truncate">
                                                            <HighlightMatch text={device.serial_number ?? device.id ?? 'Unnamed Device'} query={debouncedQuery} />
                                                        </span>
                                                    </div>
                                                </div>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </>
                            )}
                        </CommandList>
                    </Command>
                </div>,
                document.body
            )}
        </>
    );
}
